<script setup>
import { computed, ref } from 'vue';
import { parseToHsla } from 'color2k';
import { useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { dynamicTimeLocalized } from 'shared/helpers/timeHelper';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import Label from 'dashboard/components-next/label/Label.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import { useContactDetailI18n } from '../useContactDetailI18n';

const { t } = useContactDetailI18n();
const { getPlainText } = useMessageFormatter();
const route = useRoute();
const router = useRouter();
const activeFilter = ref('all');
const conversations = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const accountLabels = useMapGetter('labels/getLabels');
const labelDetails = title =>
  accountLabels.value.find(label => label.title === title) || title;
const labelTone = title => {
  const color = labelDetails(title)?.color;
  if (!color) return 'blue';
  const [hue, saturation] = parseToHsla(color);
  if (saturation < 0.15) return 'slate';
  if (hue < 20 || hue >= 340) return 'ruby';
  if (hue < 65) return 'amber';
  if (hue < 180) return 'teal';
  if (hue < 250) return 'blue';
  return 'iris';
};
const inboxById = useMapGetter('inboxes/getInboxById');
const uiFlags = useMapGetter('contactConversations/getUIFlags');
const contactConversations = computed(() =>
  conversations.value(route.params.contactId)
);
const filters = computed(() => [
  {
    value: 'all',
    label: t('CONTACT_DETAIL.ALL'),
    count: contactConversations.value.length,
  },
  {
    value: 'open',
    label: t('CONTACT_DETAIL.OPEN'),
    count: contactConversations.value.filter(item => item.status === 'open')
      .length,
  },
  {
    value: 'resolved',
    label: t('CONTACT_DETAIL.RESOLVED'),
    count: contactConversations.value.filter(item => item.status === 'resolved')
      .length,
  },
]);
const visibleConversations = computed(() =>
  contactConversations.value.filter(
    item => activeFilter.value === 'all' || item.status === activeFilter.value
  )
);
const preview = conversation => {
  const message = conversation.lastNonActivityMessage;
  const subject = conversation.customAttributes?.email?.subject;
  return getPlainText(
    subject ||
      message?.content ||
      (message?.attachments?.length
        ? t('CONTACT_DETAIL.MEDIA')
        : t('CHAT_LIST.NO_CONTENT'))
  );
};
const inboxIcon = conversation => {
  const inbox = inboxById.value(conversation.inboxId) || {};
  return getInboxIconByType(inbox.channelType, inbox.medium);
};
const conversationPath = conversation =>
  frontendURL(
    conversationUrl({ accountId: route.params.accountId, id: conversation.id })
  );
const openConversation = (event, conversation) => {
  const path = conversationPath(conversation);
  if (event.metaKey || event.ctrlKey)
    window.open(path, '_blank', 'noopener,noreferrer');
  else router.push({ path });
};
</script>

<template>
  <div
    v-if="uiFlags.isFetching"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div v-else data-testid="contact-history">
    <div class="mb-4 flex flex-wrap items-center gap-1.5">
      <button
        v-for="filter in filters"
        :key="filter.value"
        type="button"
        class="flex h-8 items-center gap-1.5 rounded-full px-3 text-xs"
        :class="
          activeFilter === filter.value
            ? 'bg-[#111827] font-semibold text-white'
            : 'bg-[#f5f5f3] text-n-slate-11'
        "
        :aria-pressed="activeFilter === filter.value"
        @click="activeFilter = filter.value"
      >
        {{ filter.label }}
        <span class="text-[11px] opacity-75">{{ filter.count }}</span>
      </button>
    </div>
    <div v-if="visibleConversations.length" class="overflow-x-auto">
      <table
        class="w-full min-w-[720px] table-fixed border-collapse text-left"
        :aria-label="t('CONTACTS_LAYOUT.SIDEBAR.TABS.HISTORY')"
      >
        <thead>
          <tr
            class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
          >
            <th class="w-[42%] pb-2 pl-14 font-semibold">
              {{ t('CONTACT_DETAIL.CONVERSATION') }}
            </th>
            <th class="w-[15%] pb-2 font-semibold">
              {{ t('CONTACT_DETAIL.PRIORITY') }}
            </th>
            <th class="w-[15%] pb-2 font-semibold">
              {{ t('CONTACT_DETAIL.STATUS') }}
            </th>
            <th class="w-[14%] pb-2 font-semibold">
              {{ t('CONTACT_DETAIL.ASSIGNEE') }}
            </th>
            <th class="pb-2 font-semibold">
              {{ t('CONTACT_DETAIL.LAST_MESSAGE') }}
            </th>
            <th class="w-5" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="conversation in visibleConversations"
            :key="conversation.id"
            tabindex="0"
            role="link"
            :aria-label="preview(conversation)"
            class="h-[68px] cursor-pointer hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-n-brand"
            @click="openConversation($event, conversation)"
            @keydown.enter="openConversation($event, conversation)"
          >
            <td class="pr-5">
              <div class="flex min-w-0 items-center gap-5">
                <span
                  class="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#edf9f0] text-[#10a344]"
                >
                  <span :class="inboxIcon(conversation)" class="size-4" />
                </span>
                <div class="min-w-0">
                  <p
                    class="m-0 truncate text-sm"
                    :class="
                      conversation.lastNonActivityMessage?.content ||
                      conversation.lastNonActivityMessage?.attachments?.length
                        ? 'text-n-slate-12'
                        : 'text-n-slate-10'
                    "
                  >
                    {{ preview(conversation) }}
                  </p>
                  <div
                    v-if="conversation.labels?.length"
                    class="mt-1 flex flex-wrap gap-1"
                  >
                    <Label
                      v-for="label in conversation.labels"
                      :key="label"
                      :label="labelDetails(label)"
                      :color="labelTone(label)"
                      compact
                      class="!h-[18px] !rounded !px-1.5 !outline-0 [&>svg]:hidden [&>span]:!text-[11px]"
                    />
                  </div>
                </div>
              </div>
            </td>
            <td class="text-xs text-n-slate-11">
              <span
                v-if="conversation.priority"
                class="inline-flex items-center gap-1.5"
                ><CardPriorityIcon
                  :priority="conversation.priority"
                  icon-size-class="size-4"
                />{{
                  t(
                    `CONVERSATION.PRIORITY.OPTIONS.${conversation.priority.toUpperCase()}`
                  )
                }}</span
              >
              <span v-else class="text-n-slate-10">&mdash;</span>
            </td>
            <td>
              <span
                class="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                :class="
                  conversation.status === 'open'
                    ? 'bg-[#edf9f0] text-[#247545]'
                    : 'bg-[#f1f2f5] text-n-slate-11'
                "
                >{{
                  ['open', 'resolved'].includes(conversation.status)
                    ? t(
                        `CONTACT_DETAIL.${conversation.status.toUpperCase()}_STATUS`
                      )
                    : t(
                        `CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${conversation.status}.TEXT`
                      )
                }}</span
              >
            </td>
            <td>
              <Avatar
                v-if="conversation.meta?.assignee"
                :name="
                  conversation.meta.assignee.name ||
                  conversation.meta.assignee.availableName
                "
                :src="conversation.meta.assignee.thumbnail"
                :size="26"
                class="[&>span[role=img]]:!bg-[#111827] [&>span[role=img]]:!text-white"
                rounded-full
                :title="conversation.meta.assignee.name"
              />
              <span v-else class="text-xs text-n-slate-10">{{
                t('CONTACT_DETAIL.NO_ASSIGNEE')
              }}</span>
            </td>
            <td class="text-xs text-n-slate-10">
              {{
                conversation.lastNonActivityMessage?.createdAt ||
                conversation.timestamp
                  ? dynamicTimeLocalized(
                      conversation.lastNonActivityMessage?.createdAt ||
                        conversation.timestamp
                    )
                  : '—'
              }}
            </td>
            <td>
              <span class="i-lucide-chevron-right size-4 text-n-slate-10" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-else class="m-0 py-10 text-center text-sm leading-6 text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.EMPTY_STATE') }}
    </p>
  </div>
</template>
